import { z } from 'zod';
import { PartStatus } from './status.ts';
import { PartImage, PartImageInput } from './image.ts';

const PART_NUMBER_PATTERN = /^[A-Za-z0-9._\-\/]+$/;

const money = z.number().nonnegative().nullable().optional();

// What the price buys: one item, a set (four tires) or a kit. Shared with
// Products. Null means not stated, and the site prints nothing after the price.
export const PriceUnit = z.enum(['each', 'set', 'kit']);
export type PriceUnit = z.infer<typeof PriceUnit>;

export const PartBase = z.object({
  part_number: z
    .string()
    .min(1)
    .max(100)
    .regex(PART_NUMBER_PATTERN, 'Part number can include letters, digits, dot, dash, slash, underscore'),
  sku: z.string().max(100).nullable().optional(),
  name: z.string().min(1).max(200),

  regular_price: money,
  sale_price: money,
  price_unit: PriceUnit.nullable().optional(),

  brand_id: z.number().int().positive().nullable(),
  category_id: z.number().int().positive().nullable(),

  fits: z.string().max(2000).nullable().optional(),
  description: z.string().min(1).max(20_000),
});

export const PartCreateInput = PartBase.extend({
  status: PartStatus.default('draft'),
  images: z.array(PartImageInput).default([]),
});
export type PartCreateInput = z.infer<typeof PartCreateInput>;

export const PartUpdateInput = PartBase.partial().extend({
  status: PartStatus.optional(),
});
export type PartUpdateInput = z.infer<typeof PartUpdateInput>;

export const DraftPartInput = PartBase.partial().extend({
  status: z.literal('draft').default('draft'),
  images: z.array(PartImageInput).optional(),
});
export type DraftPartInput = z.infer<typeof DraftPartInput>;

export const Part = PartBase.extend({
  id: z.number().int().positive(),
  slug: z.string(),
  status: PartStatus,
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  published_at: z.string().datetime().nullable(),
  brand_name: z.string().nullable(),
  category_name: z.string().nullable(),
  images: z.array(PartImage),
});
export type Part = z.infer<typeof Part>;

export const PartBulkActionInput = z.object({
  action: z.enum(['publish', 'hide', 'mark_out_of_stock', 'delete']),
  ids: z.array(z.number().int().positive()).min(1).max(500),
});
export type PartBulkActionInput = z.infer<typeof PartBulkActionInput>;

export const PartListQuery = z.object({
  q: z.string().trim().optional(),
  status: z.union([PartStatus, z.array(PartStatus)]).optional(),
  brand_id: z.coerce.number().int().positive().optional(),
  category_id: z.coerce.number().int().positive().optional(),
  min_price: z.coerce.number().nonnegative().optional(),
  max_price: z.coerce.number().nonnegative().optional(),
  part_number: z.string().optional(),
  sort: z
    .enum(['newest', 'oldest', 'price_asc', 'price_desc', 'name_asc', 'name_desc'])
    .default('newest'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(100).default(20),
});
export type PartListQuery = z.infer<typeof PartListQuery>;
